/* Vocabulary shared by server and client. Safe to import anywhere. */

export const ROLES = ["owner", "manager", "staff"] as const;
export type Role = (typeof ROLES)[number];

/** The sources the code knows. The business can recolour them and add its own (see ChannelDTO). */
export const CHANNELS = ["mmt", "booking", "airbnb", "whatsapp", "instagram", "google", "phone", "walkin", "other"] as const;
export type BuiltinChannel = (typeof CHANNELS)[number];
/** A channels.code: a built-in source, or one the business added ("hostelworld"). */
export type Channel = string;
export type ChannelGroup = "ota" | "direct";

export type ChannelMeta = { label: string; short: string; color: string; group: ChannelGroup };

/**
 * Every channel carries a 2–3 letter code as well as a colour: colour is
 * never the only signal, and these have to read on a phone in daylight.
 * These are the built-in defaults; what the business set lives in `channels`.
 */
export const CHANNEL_META: Record<BuiltinChannel, ChannelMeta> = {
  mmt: { label: "MakeMyTrip", short: "MMT", color: "var(--ch-mmt)", group: "ota" },
  booking: { label: "Booking.com", short: "BDC", color: "var(--ch-booking)", group: "ota" },
  airbnb: { label: "Airbnb", short: "ABB", color: "var(--ch-airbnb)", group: "ota" },
  whatsapp: { label: "WhatsApp", short: "WA", color: "var(--ch-whatsapp)", group: "direct" },
  instagram: { label: "Instagram", short: "IG", color: "var(--ch-instagram)", group: "direct" },
  google: { label: "Google", short: "GO", color: "var(--ch-google)", group: "direct" },
  phone: { label: "Phone", short: "PH", color: "var(--ch-phone)", group: "direct" },
  walkin: { label: "Walk-in", short: "WI", color: "var(--ch-walkin)", group: "direct" },
  other: { label: "Other", short: "OTH", color: "var(--ch-other)", group: "direct" },
};

export const isBuiltinChannel = (c: string): c is BuiltinChannel => (CHANNELS as readonly string[]).includes(c);

/** What a channel code shows as when nothing better is known: the built-in default, else the code itself. */
export function fallbackChannelMeta(code: string): ChannelMeta {
  return isBuiltinChannel(code) ? CHANNEL_META[code] : { label: code, short: code.slice(0, 3).toUpperCase(), color: "var(--ch-other)", group: "direct" };
}

export const CHANNEL_GROUP_LABEL = { ota: "Travel platforms", direct: "Direct & offline" } as const;

/**
 * Channels whose bookings can arrive through an integration, owned by the
 * platform: dates, guest and amount are read-only here. An added channel
 * (Hostelworld) is entered by hand, so it is never platform-owned.
 */
export const OTA_CHANNELS: readonly BuiltinChannel[] = ["mmt", "booking", "airbnb"];
export const isOta = (c: Channel | null | undefined): boolean => !!c && (OTA_CHANNELS as readonly string[]).includes(c);

/** Colours offered first when picking one for a channel, meal plan or extra. Any other colour can be typed in. */
export const SWATCHES = ["#a0552b", "#3f5e43", "#46545c", "#c98a1e", "#a8362c", "#2e4e8f", "#b03a5b", "#8e4470", "#1f6e6a", "#c8442f", "#6b5b95", "#7a6a57"] as const;
export const HEX_COLOR = /^#[0-9a-f]{6}$/i;

export const ROOM_KINDS = ["private", "dorm"] as const;
export type RoomKind = (typeof ROOM_KINDS)[number];

export const GENDER_POLICIES = ["any", "female", "male"] as const;
export type GenderPolicy = (typeof GENDER_POLICIES)[number];
export const GENDER_POLICY_LABEL: Record<GenderPolicy, string> = {
  any: "Mixed",
  female: "Women only",
  male: "Men only",
};

export const ADDON_UNITS = ["per_booking", "per_night", "per_person", "per_person_night"] as const;
export type AddonUnit = (typeof ADDON_UNITS)[number];
export const ADDON_UNIT_LABEL: Record<AddonUnit, string> = {
  per_booking: "per booking",
  per_night: "per night",
  per_person: "per person",
  per_person_night: "per person, per night",
};

/** Starting points offered when a stay is set up; rates are edited per stay. */
export const MEAL_PLAN_PRESETS = [
  { code: "EP", name: "Room only" },
  { code: "CP", name: "Breakfast" },
  { code: "MAP", name: "Breakfast & dinner" },
  { code: "AP", name: "All meals" },
] as const;

export const BOOKING_STATUSES = ["hold", "confirmed", "checked_in", "checked_out", "cancelled", "expired"] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/** Statuses that hold a unit. */
export const ACTIVE_STATUSES: readonly BookingStatus[] = ["hold", "confirmed", "checked_in"];

export const STATUS_LABEL: Record<BookingStatus, string> = {
  hold: "On hold",
  confirmed: "Confirmed",
  checked_in: "Checked in",
  checked_out: "Checked out",
  cancelled: "Cancelled",
  expired: "Hold lapsed",
};

export const ROLE_LABEL: Record<Role, string> = { owner: "Owner", manager: "Manager", staff: "Staff" };

/** A property the business owns, or one it rents from a landlord. */
export const OWNERSHIPS = ["owned", "leased"] as const;
export type Ownership = (typeof OWNERSHIPS)[number];
export const OWNERSHIP_LABEL: Record<Ownership, string> = { owned: "Owned", leased: "Leased" };

export const RENT_FREQUENCIES = ["monthly", "quarterly", "yearly"] as const;
export type RentFrequency = (typeof RENT_FREQUENCIES)[number];
export const RENT_FREQUENCY_LABEL: Record<RentFrequency, string> = { monthly: "a month", quarterly: "a quarter", yearly: "a year" };
/** How many rent periods fall in a year, to spread rent across days. */
export const RENT_PERIODS_PER_YEAR: Record<RentFrequency, number> = { monthly: 12, quarterly: 4, yearly: 1 };

export const PAYMENT_METHODS = ["upi", "cash", "card", "bank", "ota"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  upi: "UPI",
  cash: "Cash",
  card: "Card",
  bank: "Bank transfer",
  ota: "Paid to platform",
};

/*
 * Permissions. Staff never see money: the server strips it from every
 * response, and the client never renders it.
 */
export type Permission =
  | "money.view"
  | "rates.manage"
  | "users.manage"
  | "properties.manage"
  | "settings.manage"
  | "booking.cancelConfirmed"
  | "booking.markPaid"
  | "booking.editAmount"
  | "conflicts.resolve"
  | "activity.viewAll"
  | "spend.viewAll"
  | "spend.settle"
  /** Upkeep: hand a check to someone. */
  | "upkeep.assign"
  /** Upkeep: send the lockbox code despite a warned pre-check-in, or send a check back. */
  | "upkeep.override"
  /** Upkeep: per-room checklists, pars, intervals, laundry vendors and rates. */
  | "upkeep.setup"
  /** Staff app: run the checks handed to them and send them for review. */
  | "checks.do"
  /** Staff app: pick up open checks nobody has yet (when the rules allow it). */
  | "checks.take"
  /** Staff app: work the repairs given to them. */
  | "repairs.fix"
  /** Report something broken. */
  | "repairs.report"
  /** Send laundry out and count it back. */
  | "laundry.batches"
  /** See linen on the shelf: counts only, never the bill. */
  | "laundry.stock"
  /** See a room's lockbox code. Every look is logged. */
  | "access.view"
  /** Change a room's lockbox code, with a reason. Implies access.view. */
  | "access.rotate"
  /** Log a found item. */
  | "lost.report"
  /** Mark a found item returned. */
  | "lost.close";

/** The ten abilities a staff member can be given, one by one, in Settings › Users & roles. */
export const STAFF_PERMISSIONS = [
  "checks.do",
  "checks.take",
  "repairs.fix",
  "repairs.report",
  "laundry.batches",
  "laundry.stock",
  "access.view",
  "access.rotate",
  "lost.report",
  "lost.close",
] as const satisfies readonly Permission[];
export type StaffPermission = (typeof STAFF_PERMISSIONS)[number];
export const isStaffPermission = (p: string): p is StaffPermission => (STAFF_PERMISSIONS as readonly string[]).includes(p);

export const STAFF_PERMISSION_META: Record<StaffPermission, { label: string; sub: string }> = {
  "checks.do": { label: "Do the checks given to them", sub: "Opens the checklist and sends it for review" },
  "checks.take": { label: "Take open checks", sub: "Picks up checks nobody has yet" },
  "repairs.fix": { label: "Fix repairs given to them", sub: "Start, ask for a part, mark fixed" },
  "repairs.report": { label: "Report a repair", sub: "Tells you about something broken" },
  "laundry.batches": { label: "Send and receive laundry", sub: "Counts pieces out and back" },
  "laundry.stock": { label: "See linen on the shelf", sub: "Counts only, never the bill" },
  "access.view": { label: "See lockbox codes", sub: "Each look is logged" },
  "access.rotate": { label: "Change lockbox codes", sub: "A reason is required" },
  "lost.report": { label: "Log found items", sub: "With a photo and where it is kept" },
  "lost.close": { label: "Mark items returned", sub: "Usually the front desk does this" },
};

/** How the Access editor groups the switches, in order. `icon` is a key of components/icons.ts. */
export const STAFF_PERMISSION_GROUPS = [
  { id: "checks", label: "Room checks", icon: "checks", permissions: ["checks.do", "checks.take"] },
  { id: "repairs", label: "Repairs", icon: "repairs", permissions: ["repairs.fix", "repairs.report"] },
  { id: "laundry", label: "Laundry", icon: "laundry", permissions: ["laundry.batches", "laundry.stock"] },
  { id: "keys", label: "Lockbox codes", icon: "key", permissions: ["access.view", "access.rotate"] },
  { id: "lost", label: "Lost & found", icon: "box", permissions: ["lost.report", "lost.close"] },
] as const satisfies readonly { id: string; label: string; icon: string; permissions: readonly StaffPermission[] }[];

export const STAFF_PRESETS = ["housekeeping", "caretaker", "maintenance"] as const;
export type StaffPreset = (typeof STAFF_PRESETS)[number];
export const STAFF_PRESET_META: Record<StaffPreset, { label: string; grants: readonly StaffPermission[] }> = {
  housekeeping: { label: "Housekeeping", grants: ["checks.do", "checks.take", "repairs.report", "laundry.batches", "laundry.stock", "lost.report"] },
  caretaker: {
    label: "Caretaker",
    grants: ["checks.do", "checks.take", "repairs.fix", "repairs.report", "laundry.batches", "laundry.stock", "access.view", "access.rotate", "lost.report"],
  },
  maintenance: { label: "Maintenance", grants: ["repairs.fix", "repairs.report", "lost.report"] },
};

/**
 * The lockbox pair stays consistent: changing a code means seeing it. Turning
 * on Change also turns on See; turning off See also turns off Change.
 * `toggled` is the one switch that moved, so the rule knows which way to lean.
 */
export function coupleGrants(grants: readonly StaffPermission[], toggled?: { permission: StaffPermission; on: boolean }): StaffPermission[] {
  const set = new Set(grants);
  if (toggled?.permission === "access.view" && !toggled.on) set.delete("access.rotate");
  else if (set.has("access.rotate")) set.add("access.view");
  return STAFF_PERMISSIONS.filter((p) => set.has(p));
}

const STAFF_APP = STAFF_PERMISSIONS;

const GRANTS: Record<Role, readonly Permission[]> = {
  owner: [
    "money.view",
    "rates.manage",
    "users.manage",
    "properties.manage",
    "settings.manage",
    "booking.cancelConfirmed",
    "booking.markPaid",
    "booking.editAmount",
    "conflicts.resolve",
    "activity.viewAll",
    "spend.viewAll",
    "spend.settle",
    "upkeep.assign",
    "upkeep.override",
    "upkeep.setup",
    ...STAFF_APP,
  ],
  manager: [
    "money.view",
    "rates.manage",
    "settings.manage",
    "booking.cancelConfirmed",
    "booking.markPaid",
    "booking.editAmount",
    "conflicts.resolve",
    "activity.viewAll",
    "spend.viewAll",
    "spend.settle",
    "upkeep.assign",
    "upkeep.override",
    "upkeep.setup",
    ...STAFF_APP,
  ],
  // Staff get nothing by role: what they may do is set per person (user_grants).
  staff: [],
};

/** Who is asking: their role, and — for staff — the abilities given to them one by one. */
export type Grantee = { role: Role; grants?: readonly string[] };

export function can(actor: Grantee, permission: Permission): boolean {
  if (actor.role === "staff") return actor.grants?.includes(permission) ?? false;
  return GRANTS[actor.role].includes(permission);
}

/* ───────────────────────── Operations: issues & spend ───────────────────────── */

/** Something at a property that needs fixing. */
export const ISSUE_CATEGORIES = ["plumbing", "electrical", "water", "structure", "furniture", "appliance", "internet", "cleaning", "other"] as const;
export type IssueCategory = (typeof ISSUE_CATEGORIES)[number];
export const ISSUE_CATEGORY_LABEL: Record<IssueCategory, string> = {
  plumbing: "Plumbing",
  electrical: "Electrical",
  water: "Water & geyser",
  structure: "Roof, walls & floor",
  furniture: "Furniture & linen",
  appliance: "Appliance",
  internet: "Internet & TV",
  cleaning: "Deep clean & pests",
  other: "Other",
};

/** Urgent: guests are affected or a room can't be sold. */
export const ISSUE_PRIORITIES = ["urgent", "soon", "whenever"] as const;
export type IssuePriority = (typeof ISSUE_PRIORITIES)[number];
export const ISSUE_PRIORITY_LABEL: Record<IssuePriority, string> = { urgent: "Urgent", soon: "Soon", whenever: "Whenever" };

/**
 * open → scheduled → in_progress → fixed. `waiting` is parked on someone
 * else (parts, a contractor who hasn't called back); `dropped` was decided
 * against. Fixed and dropped are closed.
 */
export const ISSUE_STATUSES = ["open", "scheduled", "in_progress", "waiting", "fixed", "dropped"] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];
export const ISSUE_STATUS_LABEL: Record<IssueStatus, string> = {
  open: "Reported",
  scheduled: "Scheduled",
  in_progress: "Being fixed",
  waiting: "Waiting",
  fixed: "Fixed",
  dropped: "Dropped",
};
export const OPEN_ISSUE_STATUSES: readonly IssueStatus[] = ["open", "scheduled", "in_progress", "waiting"];

/** What money went on. */
export const SPEND_CATEGORIES = ["groceries", "cafe_stock", "repairs", "supplies", "laundry", "utilities", "fuel", "transport", "staff", "rent", "other"] as const;
export type SpendCategory = (typeof SPEND_CATEGORIES)[number];
export const SPEND_CATEGORY_LABEL: Record<SpendCategory, string> = {
  groceries: "Groceries",
  cafe_stock: "Café stock",
  repairs: "Repairs & parts",
  supplies: "Room supplies",
  laundry: "Laundry",
  utilities: "Electricity, gas & water",
  fuel: "Fuel",
  transport: "Transport & delivery",
  staff: "Staff advance & wages",
  rent: "Rent",
  other: "Other",
};

/** How it was paid. The same words the desk uses for guest payments, less "paid to platform". */
export const SPEND_METHODS = ["cash", "upi", "card", "bank"] as const;
export type SpendMethod = (typeof SPEND_METHODS)[number];

/**
 * Whose money it was. `own`: the person paid out of pocket and is owed it
 * back. `business`: it came from the business's account.
 */
export const SPEND_SOURCES = ["own", "business"] as const;
export type SpendSource = (typeof SPEND_SOURCES)[number];
export const SPEND_SOURCE_LABEL: Record<SpendSource, string> = { own: "Their own money", business: "Business account" };

/** Where a reimbursement stands. `none` for spends from the business account. */
export const REIMBURSEMENT_STATUSES = ["none", "pending", "paid", "rejected"] as const;
export type ReimbursementStatus = (typeof REIMBURSEMENT_STATUSES)[number];
export const REIMBURSEMENT_LABEL: Record<ReimbursementStatus, string> = { none: "—", pending: "To pay back", paid: "Paid back", rejected: "Not repaid" };

/** Files the API accepts: photos from a phone, and receipts as PDF. */
export const UPLOAD_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"] as const;
export type UploadType = (typeof UPLOAD_TYPES)[number];
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
export const MAX_FILES_PER_ITEM = 8;

/* ───────────────────────── Routine checks, handover, lost & found ───────────────────────── */

/** How often a routine check comes round, in days. */
export const PLAN_INTERVALS = [
  { days: 7, label: "Every week" },
  { days: 14, label: "Every 2 weeks" },
  { days: 30, label: "Every month" },
  { days: 90, label: "Every 3 months" },
  { days: 180, label: "Every 6 months" },
  { days: 365, label: "Every year" },
] as const;
export const intervalLabel = (days: number) => PLAN_INTERVALS.find((p) => p.days === days)?.label ?? `Every ${days} days`;

/** held: in the cupboard · returned: back with the guest · handed_over: to police or management · disposed: thrown or donated after the wait */
export const LOST_STATUSES = ["held", "returned", "handed_over", "disposed"] as const;
export type LostStatus = (typeof LOST_STATUSES)[number];
export const LOST_STATUS_LABEL: Record<LostStatus, string> = { held: "Held", returned: "Returned", handed_over: "Handed over", disposed: "Disposed" };

/* ───────────────────────── Upkeep: room checks & laundry ───────────────────────── */

/**
 * Every stay makes a chain of checks: after checkout, the clean and
 * room-ready check, then pre-check-in before the next arrival. Mid-stay
 * service and deep cleans sit beside the chain.
 */
export const CHECK_STAGES = ["checkout", "ready", "pre_checkin", "mid_stay", "deep_clean"] as const;
export type CheckStage = (typeof CHECK_STAGES)[number];
export const CHECK_STAGE_LABEL: Record<CheckStage, string> = {
  checkout: "Checkout check",
  ready: "Clean + room-ready",
  pre_checkin: "Pre-check-in",
  mid_stay: "Mid-stay service",
  deep_clean: "Deep clean",
};
/** The short form: a Plan pill, a log label ("A2 Deep clean"). */
export const CHECK_STAGE_SHORT: Record<CheckStage, string> = {
  checkout: "Checkout",
  ready: "Room-ready",
  pre_checkin: "Pre-check-in",
  mid_stay: "Mid-stay",
  deep_clean: "Deep clean",
};

export const CHECK_TASK_STATUSES = ["open", "assigned", "in_progress", "submitted", "passed", "warned", "overridden", "sent_back", "cancelled"] as const;
export type CheckTaskStatus = (typeof CHECK_TASK_STATUSES)[number];
/** Statuses where the work is still to do. */
export const OPEN_CHECK_STATUSES: readonly CheckTaskStatus[] = ["open", "assigned", "in_progress"];
/** Statuses where the check has been handed in. */
export const DONE_CHECK_STATUSES: readonly CheckTaskStatus[] = ["submitted", "passed", "warned", "overridden", "sent_back"];

export const CHECK_ANSWERS = ["ok", "attention", "fixed", "missing"] as const;
export type CheckAnswer = (typeof CHECK_ANSWERS)[number];
export const CHECK_ANSWER_LABEL: Record<CheckAnswer, string> = { ok: "OK", attention: "Needs attention", fixed: "Fixed now", missing: "Missing" };

export const FINDING_KINDS = ["missing", "attention", "consumable"] as const;
export type FindingKind = (typeof FINDING_KINDS)[number];
export const FINDING_ACTIONS = ["charge", "repair", "restock", "none"] as const;
export type FindingAction = (typeof FINDING_ACTIONS)[number];

/** Where a piece of linen is. Stock by place is the sum of the ledger's moves. */
export const LAUNDRY_LOCATIONS = ["clean", "in_room", "dirty", "vendor", "in_house"] as const;
export type LaundryLocation = (typeof LAUNDRY_LOCATIONS)[number];
export const LAUNDRY_LOCATION_LABEL: Record<LaundryLocation, string> = { clean: "Clean", in_room: "In rooms", dirty: "Dirty", vendor: "At vendor", in_house: "In-house" };

export const LAUNDRY_BATCH_KINDS = ["vendor", "in_house"] as const;
export type LaundryBatchKind = (typeof LAUNDRY_BATCH_KINDS)[number];
export const LAUNDRY_BATCH_STATUSES = ["out", "received", "short", "settled"] as const;
export type LaundryBatchStatus = (typeof LAUNDRY_BATCH_STATUSES)[number];

/**
 * The guest's code: waiting for pre-check-in, held on a warning, ready to
 * go once it passes or is overridden, then sent (or failed to send).
 */
export const LOCKBOX_STATUSES = ["waiting", "held", "ready", "sent", "failed"] as const;
export type LockboxStatus = (typeof LOCKBOX_STATUSES)[number];

/** Why a room's lockbox code changed. Required on every change. */
export const LOCKBOX_REASONS = ["routine", "checked_out", "shared", "lock_replaced"] as const;
export type LockboxReason = (typeof LOCKBOX_REASONS)[number];
export const LOCKBOX_REASON_LABEL: Record<LockboxReason, string> = {
  routine: "Routine",
  checked_out: "Guest checked out",
  shared: "Code shared by mistake",
  lock_replaced: "Lock replaced",
};

/** How a laundry vendor bills: each batch, or a weekly or monthly bill. Sets how the Due figure groups batches. */
export const LAUNDRY_BILLING = ["batch", "weekly", "monthly"] as const;
export type LaundryBilling = (typeof LAUNDRY_BILLING)[number];
export const LAUNDRY_BILLING_LABEL: Record<LaundryBilling, string> = { batch: "Pay each batch", weekly: "Weekly bill", monthly: "Monthly bill" };

/*
 * The board's columns and the moves between them. The server enforces these;
 * the client mirrors them to light up where a card can go.
 */
export const BOARD_COLUMNS = ["todo", "assigned", "in_progress", "review", "done"] as const;
export type BoardColumn = (typeof BOARD_COLUMNS)[number];
export const BOARD_COLUMN_LABEL: Record<BoardColumn, string> = { todo: "To do", assigned: "Assigned", in_progress: "In progress", review: "Review", done: "Done" };

/** Which column a check sits in; null for cancelled (hidden). */
export function columnOf(status: CheckTaskStatus, assigned: boolean): BoardColumn | null {
  switch (status) {
    case "open":
      return assigned ? "assigned" : "todo";
    case "assigned":
      return "assigned";
    case "in_progress":
    case "sent_back":
      return "in_progress";
    case "submitted":
    case "warned":
      return "review";
    case "passed":
    case "overridden":
      return "done";
    case "cancelled":
      return null;
  }
}

/** The label for a move from one column to another, or null when the move isn't allowed by hand. */
export function moveLabel(from: BoardColumn, to: BoardColumn): string | null {
  if (from === "todo" && to === "assigned") return "Assign";
  if (from === "assigned" && to === "todo") return "Unassign";
  if (from === "assigned" && to === "in_progress") return "Start";
  if (from === "in_progress" && to === "assigned") return "Pause";
  if (from === "review" && to === "done") return "Approve";
  if (from === "review" && to === "in_progress") return "Send back";
  return null;
}

/** A section's name tells a repair its trade: Appliances → appliance, Bathroom → plumbing. */
export function issueCategoryForSection(section: string): IssueCategory {
  const s = section.toLowerCase();
  if (/applian|kitchen|utensil/.test(s)) return "appliance";
  if (/bath|toilet|wash/.test(s)) return "plumbing";
  if (/electric|light|lamp|power/.test(s)) return "electrical";
  if (/wifi|wi-fi|internet|screen|tv|projector|game/.test(s)) return "internet";
  if (/bed|living|furniture|common/.test(s)) return "furniture";
  if (/clean|feel|utility/.test(s)) return "cleaning";
  if (/water|geyser/.test(s)) return "water";
  return "other";
}

/** The WhatsApp message a guest gets with their code, before a property writes its own. */
export const DEFAULT_LOCKBOX_TEMPLATE =
  "Hi {guestFirst}, welcome to {property}! Your room {room} is ready. The key is in {lockboxLabel}, code {code}. Wi-Fi: {wifiName} / {wifiPassword}. Check-out is at {checkoutTime}. Anything at all, call {caretakerPhone}.";

/* ───────────────────────── The activity log ───────────────────────── */

/** What an activity entry is about. */
export const ACTIVITY_ENTITIES = [
  "booking",
  "rate",
  "user",
  "property",
  "room",
  "guest",
  "conflict",
  "session",
  "settings",
  "channel",
  "issue",
  "spend",
  "upkeep",
  "lost",
  "laundry",
  "checklist",
  "rules",
  "handover",
] as const;

/** The Activity log's Area chips: each a set of entity types, so the log needs no area column. */
export const ACTIVITY_AREAS = {
  bookings: { label: "Bookings", types: ["booking", "guest", "conflict", "channel"] },
  checks: { label: "Room checks", types: ["upkeep", "checklist", "rules"] },
  repairs: { label: "Repairs", types: ["issue"] },
  lost: { label: "Lost & found", types: ["lost"] },
  laundry: { label: "Laundry", types: ["laundry"] },
  settings: { label: "Settings", types: ["settings", "property", "room", "rate", "user"] },
  session: { label: "Sign-in", types: ["session"] },
} as const satisfies Record<string, { label: string; types: readonly (typeof ACTIVITY_ENTITIES)[number][] }>;
export type ActivityArea = keyof typeof ACTIVITY_AREAS;
export const ACTIVITY_AREA_IDS = Object.keys(ACTIVITY_AREAS) as ActivityArea[];

/** The area an entry's entity type belongs to; spend and handover sit in none but All. */
export function activityAreaOf(entityType: string): ActivityArea | null {
  for (const id of ACTIVITY_AREA_IDS) if ((ACTIVITY_AREAS[id].types as readonly string[]).includes(entityType)) return id;
  return null;
}

/* ───────────────────────── Room check rules ───────────────────────── */

/** A room's chain on the Plan: checkout, deep clean, room-ready, mid-stay, pre-check-in. */
export const PLAN_STAGE_ORDER: Record<CheckStage, number> = { checkout: 0, deep_clean: 1, ready: 2, mid_stay: 3, pre_checkin: 4 };

/** "A2 Deep clean": how the log and the toasts name a check. */
export const checkLabel = (roomCode: string, stage: CheckStage) => `${roomCode} ${CHECK_STAGE_SHORT[stage]}`;

export const CHECKOUT_TIMINGS = ["at_checkout", "after_30m"] as const;
export type CheckoutTiming = (typeof CHECKOUT_TIMINGS)[number];
export const READY_TIMINGS = ["before_arrival_2h", "by_1500", "within_3h"] as const;
export type ReadyTiming = (typeof READY_TIMINGS)[number];
export const PRE_TIMINGS = ["before_30m", "before_1h"] as const;
export type PreTiming = (typeof PRE_TIMINGS)[number];
export const MID_EVERY = [2, 1, 3] as const;
export const DEEP_EVERY = [14, 7, 30] as const;

export const TIMING_LABEL: Record<CheckoutTiming | ReadyTiming | PreTiming, string> = {
  at_checkout: "At check-out time",
  after_30m: "30 min after check-out",
  before_arrival_2h: "2 h before the next arrival",
  by_1500: "By 15:00",
  within_3h: "Within 3 h of checkout",
  before_30m: "30 min before arrival",
  before_1h: "1 h before arrival",
};
export const midLabel = (n: number) => (n === 1 ? "Every night" : `Every ${n} nights`);
export const deepLabel = (n: number) => `Every ${n} days`;

/** When the room's usual person is off or at the limit: the least-loaded person, or leave it for the manager. */
export const CHECK_FALLBACKS = ["fewest", "open"] as const;
export type CheckFallback = (typeof CHECK_FALLBACKS)[number];
export const CHECK_FALLBACK_LABEL: Record<CheckFallback, string> = { fewest: "Whoever has the fewest", open: "Leave it open for me" };
